import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Download, LogOut, Search, Users, Sparkles, Share2, MousePointerClick } from "lucide-react";
import { ADMIN } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, checking, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [shareStats, setShareStats] = useState(null);
  const [clickStats, setClickStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (checking) return;
    if (!user || user.role !== "admin") {
      navigate("/admin/login");
      return;
    }
    (async () => {
      try {
        const [a, b, c, d] = await Promise.all([
          api.get("/admin/leads"),
          api.get("/admin/stats"),
          api.get("/admin/share-stats"),
          api.get("/admin/tool-click-stats"),
        ]);
        setLeads(a.data.leads || []);
        setStats(b.data);
        setShareStats(c.data);
        setClickStats(d.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, checking, navigate]);

  async function handleLogout() {
    await logout();
    navigate("/admin/login");
  }

  function exportCsv() {
    // Build form, post w/ credentials so cookie is sent
    window.open(`${BACKEND_URL}/api/admin/leads.csv`, "_blank");
  }

  const filtered = leads.filter((l) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      (l.name || "").toLowerCase().includes(q) ||
      (l.email || "").toLowerCase().includes(q) ||
      (l.company || "").toLowerCase().includes(q) ||
      (l.recommended_tool_names || []).join(" ").toLowerCase().includes(q)
    );
  });

  if (checking || loading) {
    return (
      <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid={ADMIN.dashboard}>
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[#0f766e] grid place-items-center text-white">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <div className="font-display font-semibold text-slate-900 text-sm leading-tight">
                AI Operator Hub
              </div>
              <div className="text-xs text-slate-500 leading-tight">Admin dashboard</div>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            data-testid={ADMIN.logoutBtn}
            className="text-slate-600 hover:text-slate-900"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                Total leads
              </span>
              <Users className="w-4 h-4 text-[#0f766e]" />
            </div>
            <div
              data-testid={ADMIN.statTotal}
              className="font-display mt-3 text-4xl font-bold text-slate-900"
            >
              {stats?.total_leads ?? 0}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-6 md:col-span-2">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
              Top use cases
            </span>
            <div className="mt-3 space-y-2">
              {(stats?.by_use_case || []).slice(0, 4).map((row) => (
                <div key={row.use_case} className="flex items-center gap-3">
                  <span className="text-sm text-slate-700 w-32 shrink-0">
                    {row.use_case}
                  </span>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#0f766e]"
                      style={{
                        width: `${Math.min(
                          100,
                          (row.count / Math.max(1, stats.total_leads)) * 100
                        )}%`,
                      }}
                    />
                  </div>
                  <span className="text-sm text-slate-500 w-8 text-right">
                    {row.count}
                  </span>
                </div>
              ))}
              {(!stats?.by_use_case || stats.by_use_case.length === 0) && (
                <p className="text-sm text-slate-500">No data yet.</p>
              )}
            </div>
          </div>
        </div>

        {/* Share opens */}
        <div
          data-testid="admin-share-stats"
          className="mt-6 bg-white border border-slate-200 rounded-xl p-6"
        >
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                Share opens
              </span>
              <div className="flex items-baseline gap-3 mt-2">
                <Share2 className="w-4 h-4 text-[#0f766e]" />
                <span
                  data-testid="admin-share-total"
                  className="font-display text-3xl font-bold text-slate-900"
                >
                  {shareStats?.total ?? 0}
                </span>
                <span className="text-sm text-slate-500">
                  human opens · {shareStats?.bot_opens ?? 0} crawler previews
                </span>
              </div>
            </div>
          </div>

          {shareStats && shareStats.by_ref && shareStats.by_ref.length > 0 ? (
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {shareStats.by_ref.map((row) => {
                const pct = Math.min(
                  100,
                  (row.count / Math.max(1, shareStats.total)) * 100
                );
                return (
                  <div
                    key={row.ref}
                    data-testid={`admin-share-ref-${row.ref}`}
                    className="border border-slate-200 rounded-lg p-4 bg-slate-50/40"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-800">
                        {row.label}
                      </span>
                      <span className="text-sm font-semibold text-[#0f766e]">
                        {row.count}
                      </span>
                    </div>
                    <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full bg-[#0f766e]"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="mt-4 text-sm text-slate-500">
              No share opens yet. Encourage users to share their results to start seeing channel performance here.
            </p>
          )}

          {shareStats?.top_leads && shareStats.top_leads.length > 0 && (
            <div className="mt-6 pt-5 border-t border-slate-100">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                Top shared results
              </span>
              <div className="mt-3 space-y-2">
                {shareStats.top_leads.map((row) => (
                  <div
                    key={row.lead_id}
                    className="flex items-center justify-between text-sm"
                  >
                    <div className="flex items-center gap-2 text-slate-700">
                      <span className="font-medium">{row.first_name}</span>
                      <span className="text-slate-400 hidden sm:inline">·</span>
                      <span className="text-slate-500 hidden sm:inline">
                        {row.tools.join(" · ")}
                      </span>
                    </div>
                    <span className="text-[#0f766e] font-semibold">{row.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Tool clicks */}
        <div
          data-testid="admin-tool-clicks"
          className="mt-6 bg-white border border-slate-200 rounded-xl p-6"
        >
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                Tool clicks
              </span>
              <div className="flex items-baseline gap-3 mt-2">
                <MousePointerClick className="w-4 h-4 text-[#0f766e]" />
                <span
                  data-testid="admin-tool-clicks-total"
                  className="font-display text-3xl font-bold text-slate-900"
                >
                  {clickStats?.total ?? 0}
                </span>
                <span className="text-sm text-slate-500">
                  total clicks on recommended tool CTAs
                </span>
              </div>
            </div>
          </div>

          {clickStats && clickStats.by_tool && clickStats.by_tool.length > 0 ? (
            <div className="mt-5 space-y-2">
              {clickStats.by_tool.map((row) => {
                const pct = Math.min(
                  100,
                  (row.count / Math.max(1, clickStats.total)) * 100
                );
                return (
                  <div
                    key={row.tool_id}
                    data-testid={`admin-tool-click-${row.tool_id}`}
                    className="flex items-center gap-3"
                  >
                    <span className="text-sm text-slate-700 w-32 shrink-0 truncate">
                      {row.name}
                    </span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#0f766e]"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-sm text-slate-500 w-12 text-right tabular-nums">
                      {row.count}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="mt-4 text-sm text-slate-500">
              No tool clicks yet. They will appear here when users click a recommended tool CTA.
            </p>
          )}

          {clickStats?.by_ref && clickStats.by_ref.length > 0 && (
            <div className="mt-6 pt-5 border-t border-slate-100">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                Click attribution
              </span>
              <div className="mt-3 flex flex-wrap gap-2">
                {clickStats.by_ref.map((row) => (
                  <span
                    key={row.ref}
                    className="text-xs px-2.5 py-1 rounded-full bg-[#ccfbf1] text-[#0f766e] font-medium"
                  >
                    {row.label}: {row.count}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Toolbar */}
        <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <h2 className="font-display text-2xl font-semibold text-slate-900">Leads</h2>
          <div className="flex gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, email, tool…"
                className="pl-9 h-10 w-64 border-slate-200 focus-visible:ring-[#0f766e]"
              />
            </div>
            <Button
              onClick={exportCsv}
              data-testid={ADMIN.exportCsvBtn}
              className="bg-[#0f766e] hover:bg-[#115e59] text-white h-10"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="mt-5 bg-white border border-slate-200 rounded-xl overflow-hidden">
          <Table data-testid={ADMIN.leadsTable}>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-slate-600">Date</TableHead>
                <TableHead className="text-slate-600">Name</TableHead>
                <TableHead className="text-slate-600">Email</TableHead>
                <TableHead className="text-slate-600">Company</TableHead>
                <TableHead className="text-slate-600">Use case</TableHead>
                <TableHead className="text-slate-600">Recommended</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-slate-500 py-10">
                    No leads yet. Share the quiz link to start collecting matches.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((l) => (
                <TableRow key={l.id} className="hover:bg-slate-50">
                  <TableCell className="text-sm text-slate-500 whitespace-nowrap">
                    {formatDate(l.created_at)}
                  </TableCell>
                  <TableCell className="font-medium text-slate-900">{l.name}</TableCell>
                  <TableCell className="text-slate-700">{l.email}</TableCell>
                  <TableCell className="text-slate-700">{l.company || "—"}</TableCell>
                  <TableCell className="text-slate-700">
                    {l.answers?.use_case || "—"}
                  </TableCell>
                  <TableCell className="text-slate-700">
                    <div className="flex flex-wrap gap-1">
                      {(l.recommended_tool_names || []).map((n) => (
                        <span
                          key={n}
                          className="text-xs px-2 py-0.5 rounded-full bg-[#ccfbf1] text-[#0f766e] font-medium"
                        >
                          {n}
                        </span>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
}
